import { useState } from 'react';
import { FieldExtensionComponentProps } from "@backstage/plugin-scaffolder-react";
import {
	fetchApiRef,
	useApi,
  } from '@backstage/core-plugin-api';
  import {
	Progress,
	ErrorPanel,
  } from '@backstage/core-components';
import { useAsync } from 'react-use';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { sortBy } from 'lodash';
import { z } from 'zod';
import { AzureDevOpsOauthWrapper } from '../AzureDevOpsOauthWrapper';
import { useAzureDevOpsOauthContext } from '../../hooks/useOauthContext';
import { AzureDevOpsProjectFieldComponent, AzureDevOpsProjectFieldScopes } from '../AzureDevOpsProjectField';
import { azureDevOpsFetch } from '../helpers';

const uiOptionsSchema = z.object({
	organization: z.string().describe('The Azure DevOps organization to query'),
	project: z.string().optional().describe('The Azure DevOps project to query'),
	title: z.string().optional().describe('The title of the field'),
	tokenSecretName: z.string().optional().describe('The name of the secret to store the token in'),
	extraScopes: z.array(z.string()).optional().describe('Additional OAuth scopes to request'),
});

export const AzureDevOpsRepoFieldSchema = makeFieldSchema({
	output: x => x.string(),
	uiOptions: zImpl => zImpl.object({
		organization: zImpl.string().describe('The Azure DevOps organization to query'),
		project: zImpl.string().optional().describe('The Azure DevOps project to query'),
		title: zImpl.string().optional().describe('The title of the field'),
		tokenSecretName: zImpl.string().optional().describe('The name of the secret to store the token in'),
		extraScopes: zImpl.array(zImpl.string()).optional().describe('Additional OAuth scopes to request'),
	}),
});

export const AzureDevOpsRepoFieldScopes = ['https://app.vssps.visualstudio.com/vso.code'];

export const AzureDevOpsRepoFieldComponent = (
	props: FieldExtensionComponentProps<string, { organization: string, project?: string }> & {
		project?: string,
	},
) => {
	const fetchApi = useApi(fetchApiRef);
	const { token } = useAzureDevOpsOauthContext();
	const [repos, setRepos] = useState<{ label: string, value: string }[]>([]);
	const project = props.uiSchema['ui:options']?.project ?? props.project

	const { error } = useAsync(async () => {
		if (!project) {
			return;
		}

		const organization = props.uiSchema['ui:options']?.organization;		
		const body = await azureDevOpsFetch({
			url: `https://dev.azure.com/${organization}/${project}/_apis/git/repositories?api-version=7.2-preview.1`,
			token,
			fetchApi,
		});
		const fetchedRepos = body.value.map((repo: any) => ({
			label: repo.name,
			value: repo.name,
		}));
		setRepos(sortBy(fetchedRepos, 'label'));
	}, [project, token]);

	if (!project) {
		return <></>;
	}

	if (error) {
		return <ErrorPanel error={error} />;
	}
	if (!repos) {
		return <Progress />;
	}

	return (
		<FormControl
			margin="normal"
			required={props.required}
			error={(props?.rawErrors?.length || 0) > 0 && !props.formData}
		>
			<Autocomplete
				options={repos}
				freeSolo={false}
				renderInput={(params) => <TextField {...params} label="Azure DevOps Repository" variant="outlined" />}
				getOptionLabel={(option) => option.label}
				onChange={(_, value) => {
					props.onRepoChange?.(value?.value);
					props.onChange(`${project}/${value?.value}`);
				}}
			/>
			<FormHelperText>Select a repository from the list.</FormHelperText>
		</FormControl>
	)
};

export const AzureDevOpsRepoField = (props: FieldExtensionComponentProps<string, {
	organization: string,
	project?: string,
	tokenSecretName?: string,
	extraScopes? : string[],
	} & {
	onRepoChange?: (value: string) => void;
}>) => {
	const [project, setProject] = useState('');

	if (!props.uiSchema) {
		return <ErrorPanel error={new Error("No uiSchema provided")} />;
	}
	
	const result = uiOptionsSchema.safeParse(props.uiSchema['ui:options']);
	const tokenSecretName = props.uiSchema['ui:options']?.tokenSecretName;
	
	if (result.success) {
		const addProjectField = !props.uiSchema['ui:options']?.project;
		if (addProjectField) {
			const scopes = Array.from(
				new Set(
					AzureDevOpsRepoFieldScopes
						.concat(...AzureDevOpsProjectFieldScopes)
						.concat(props.uiSchema['ui:options']?.extraScopes ?? [])));
			const projectChangeHandler = (value: string) => {
				setProject(value);
			};
			return (
				<AzureDevOpsOauthWrapper {...props} scopes={scopes} tokenSecretName={tokenSecretName}>
					<AzureDevOpsProjectFieldComponent {...props} onProjectChange={projectChangeHandler} />
					<AzureDevOpsRepoFieldComponent {...props} project={project} />
				</AzureDevOpsOauthWrapper>
			)
		}
		
		return (
			<AzureDevOpsOauthWrapper {...props} scopes={AzureDevOpsRepoFieldScopes}>
				<AzureDevOpsRepoFieldComponent {...props} />
			</AzureDevOpsOauthWrapper>
		)
	}

	return <ErrorPanel error={result.error} />;
};
